import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Dirty tracking for create/add forms.
 *
 * While `keepBaselining` is true (form still at auto defaults / empty),
 * the baseline snapshot is refreshed so async hydration (VAT, currency,
 * default reporting category, etc.) does NOT count as unsaved changes.
 *
 * Once the user changes something and `keepBaselining` becomes false,
 * the baseline freezes and normal dirty detection applies.
 *
 * @param {() => string} getFormSnapshot
 * @param {boolean} keepBaselining
 */
export default function useCreateFormDirtyBaseline(getFormSnapshot, keepBaselining) {
  const initialFormSnapshotRef = useRef(null);
  const [, setEpoch] = useState(0);

  useEffect(() => {
    if (!(keepBaselining || initialFormSnapshotRef.current === null)) return;
    const next = getFormSnapshot();
    if (initialFormSnapshotRef.current === next) return;
    initialFormSnapshotRef.current = next;
    setEpoch((n) => n + 1);
  }, [getFormSnapshot, keepBaselining]);

  const isDirty =
    !keepBaselining &&
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const discardBaseline = useCallback(() => {
    initialFormSnapshotRef.current = getFormSnapshot();
    setEpoch((n) => n + 1);
  }, [getFormSnapshot]);

  const syncBaselineAfterSave = useCallback(() => {
    initialFormSnapshotRef.current = getFormSnapshot();
    setEpoch((n) => n + 1);
  }, [getFormSnapshot]);

  return {
    isDirty,
    initialFormSnapshotRef,
    discardBaseline,
    syncBaselineAfterSave,
  };
}
